import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { bootstrapSession, logoutRequest } from './api';
import { authAdapter } from './memory-access-adapter';

export type AdminRole = 'SUPERADMIN' | 'CITY_MODERATOR' | 'CRM_MANAGER';

export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName?: string | null;
  username?: string | null;
}

export interface Session {
  accessToken: string;
  user: AuthUser;
  adminRoles: AdminRole[];
}

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  adminRoles: AdminRole[];
  setSession: (s: Session) => void;
  clearSession: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);
  // Пока true — рендерим splash вместо роутера. Это нужно чтобы route-guard
  // не отдал ложный редирект на /login до того как bootstrapSession() поднимет
  // access из refresh-cookie.
  const [isHydrating, setIsHydrating] = useState(true);

  const setSession = useCallback((s: Session) => {
    authAdapter.setAccess(s.accessToken);
    setUser(s.user);
    setAdminRoles(s.adminRoles);
  }, []);

  const clearSession = useCallback(() => {
    authAdapter.clear();
    setUser(null);
    setAdminRoles([]);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      /* ignore */
    }
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    authAdapter.onRefreshFailHandler = () => {
      setUser(null);
      setAdminRoles([]);
    };
    return () => {
      delete authAdapter.onRefreshFailHandler;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    bootstrapSession().then((s) => {
      if (cancelled) return;
      if (s) setSession(s);
      setIsHydrating(false);
    });
    return () => {
      cancelled = true;
    };
  }, [setSession]);

  const value: AuthState = {
    isAuthenticated: user !== null,
    user,
    adminRoles,
    setSession,
    clearSession,
    logout,
  };

  if (isHydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Загрузка…
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
